import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Save, X, Check, AlertCircle, Settings, Database, Download, Upload } from 'lucide-react';
import { securityServicesData } from '../../data/securityServicesData';
import { 
  getSecurityServicesData, 
  saveSecurityServicesData, 
  resetSecurityServicesData,
  exportSecurityServicesData,
  importSecurityServicesData,
  hasSavedChanges
} from '../../utils/securityServicesDataManager';

/**
 * SecurityServicesAdmin - Admin interface for managing services matrix data
 * Allows adding, editing, and deleting services and features
 */
const SecurityServicesAdmin = ({ onDataUpdate }) => {
  const [activeTab, setActiveTab] = useState('services');
  
  // Initialize with current data (either saved or default)
  const currentData = getSecurityServicesData();
  const [services, setServices] = useState(currentData.services);
  const [features, setFeatures] = useState(currentData.features);
  const [featureServices, setFeatureServices] = useState(currentData.featureServices);
  const [categories, setCategories] = useState(currentData.categories);
  
  // Edit states
  const [editingService, setEditingService] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  const [showAddService, setShowAddService] = useState(false);
  const [showAddFeature, setShowAddFeature] = useState(false);
  
  // Form states
  const [serviceForm, setServiceForm] = useState({ id: '', name: '', category: '', description: '' });
  const [featureForm, setFeatureForm] = useState({ id: '', name: '', category: '', description: '' });
  
  // Notification state
  const [notification, setNotification] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Auto-save timer ref
  const autoSaveTimeoutRef = useRef(null);

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Generate ID from name
  const generateId = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .trim();
  };

  // Validate feature form
  const validateFeatureForm = (form) => {
    const errors = {};
    
    if (!form.name.trim()) {
      errors.name = 'Feature name is required';
    } else if (form.name.trim().length < 3) {
      errors.name = 'Feature name must be at least 3 characters';
    } else if (form.name.trim().length > 100) {
      errors.name = 'Feature name must be less than 100 characters';
    }
    
    if (!form.category.trim()) {
      errors.category = 'Category is required';
    }
    
    if (form.description && form.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }
    
    return errors;
  };

  // Validate service form
  const validateServiceForm = (form) => {
    const errors = {};
    
    if (!form.name.trim()) {
      errors.name = 'Service name is required';
    } else if (form.name.trim().length < 3) {
      errors.name = 'Service name must be at least 3 characters';
    } else if (form.name.trim().length > 100) {
      errors.name = 'Service name must be less than 100 characters';
    }
    
    if (form.description && form.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }
    
    return errors;
  };

  // Service CRUD operations
  const handleAddService = () => {
    // Validate form
    const validationErrors = validateServiceForm(serviceForm);
    if (Object.keys(validationErrors).length > 0) {
      showNotification(`Validation failed: ${Object.values(validationErrors)[0]}`, 'error');
      return;
    }

    const newId = serviceForm.id || generateId(serviceForm.name);
    const newService = {
      id: newId,
      name: serviceForm.name.trim(),
      category: serviceForm.category || 'ASI Services',
      description: serviceForm.description.trim()
    };

    // Check for duplicate ID
    if (services.find(s => s.id === newId)) {
      showNotification('Service ID already exists', 'error');
      return;
    }

    // Check for duplicate name
    if (services.find(s => s.name.toLowerCase() === serviceForm.name.trim().toLowerCase())) {
      showNotification('A service with this name already exists', 'error');
      return;
    }

    setServices([...services, newService]);
    setServiceForm({ id: '', name: '', category: '', description: '' });
    setShowAddService(false);
    
    // Mark as having unsaved changes and trigger auto-save
    setHasUnsavedChanges(true);
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      handleAutoSave();
    }, 1000);
    
    showNotification('Service added successfully');
  };

  const handleEditService = (service) => {
    setEditingService(service.id);
    setServiceForm(service);
  };

  const handleUpdateService = () => {
    // Validate form
    const validationErrors = validateServiceForm(serviceForm);
    if (Object.keys(validationErrors).length > 0) {
      showNotification(`Validation failed: ${Object.values(validationErrors)[0]}`, 'error');
      return;
    }

    // Check for duplicate name (excluding current service)
    if (services.find(s => s.id !== editingService && s.name.toLowerCase() === serviceForm.name.trim().toLowerCase())) {
      showNotification('A service with this name already exists', 'error');
      return;
    }

    setServices(services.map(s => 
      s.id === editingService 
        ? { ...s, name: serviceForm.name.trim(), category: serviceForm.category, description: serviceForm.description.trim() }
        : s
    ));
    setEditingService(null);
    setServiceForm({ id: '', name: '', category: '', description: '' });
    
    // Mark as having unsaved changes and trigger auto-save
    setHasUnsavedChanges(true);
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
      handleAutoSave();
    }, 1000);
    
    showNotification('Service updated successfully');
  };

  const handleDeleteService = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    const serviceName = service ? service.name : 'this service';
    
    if (window.confirm(`Are you sure you want to delete "${serviceName}"? This will also remove all feature mappings for this service and cannot be undone.`)) {
      setServices(services.filter(s => s.id !== serviceId));
      
      // Remove service from feature mappings
      const updatedFeatureServices = { ...featureServices };
      Object.keys(updatedFeatureServices).forEach(featureId => {
        if (updatedFeatureServices[featureId][serviceId]) {
          delete updatedFeatureServices[featureId][serviceId];
        }
      });
      setFeatureServices(updatedFeatureServices);
      
      // Mark as having unsaved changes and trigger auto-save
      setHasUnsavedChanges(true);
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleAutoSave();
      }, 1000);
      
      showNotification(`Service "${serviceName}" deleted successfully`);
    }
  };

  // Feature CRUD operations
  const handleAddFeature = async () => {
    setIsLoading(true);
    try {
      // Validate form
      const validationErrors = validateFeatureForm(featureForm);
      if (Object.keys(validationErrors).length > 0) {
        showNotification(`Validation failed: ${Object.values(validationErrors)[0]}`, 'error');
        return;
      }

      const newId = featureForm.id || generateId(featureForm.name);
      const newFeature = {
        id: newId,
        name: featureForm.name.trim(),
        category: featureForm.category || 'General',
        description: featureForm.description?.trim() || ''
      };

      // Check for duplicate ID
      if (features.find(f => f.id === newId)) {
        showNotification('Feature ID already exists', 'error');
        return;
      }

      // Check for duplicate name
      if (features.find(f => f.name.toLowerCase() === featureForm.name.trim().toLowerCase())) {
        showNotification('A feature with this name already exists', 'error');
        return;
      }

      setFeatures([...features, newFeature]);
      
      // Add empty feature mapping
      setFeatureServices({
        ...featureServices,
        [newId]: {}
      });

      // Add category if new
      if (featureForm.category && !categories.features.includes(featureForm.category)) {
        setCategories({
          ...categories,
          features: [...categories.features, featureForm.category]
        });
      }

      setFeatureForm({ id: '', name: '', category: '', description: '' });
      setShowAddFeature(false);
      
      // Mark as having unsaved changes and trigger auto-save
      setHasUnsavedChanges(true);
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleAutoSave();
      }, 1000);
      
      showNotification('Feature added successfully');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditFeature = (feature) => {
    setEditingFeature(feature.id);
    setFeatureForm(feature);
  };

  const handleUpdateFeature = async () => {
    setIsLoading(true);
    try {
      // Validate form
      const validationErrors = validateFeatureForm(featureForm);
      if (Object.keys(validationErrors).length > 0) {
        showNotification(`Validation failed: ${Object.values(validationErrors)[0]}`, 'error');
        return;
      }

      // Check for duplicate name (excluding current feature)
      if (features.find(f => f.id !== editingFeature && f.name.toLowerCase() === featureForm.name.trim().toLowerCase())) {
        showNotification('A feature with this name already exists', 'error');
        return;
      }

      setFeatures(features.map(f => 
        f.id === editingFeature 
          ? { ...f, name: featureForm.name.trim(), category: featureForm.category, description: featureForm.description?.trim() || '' }
          : f
      ));

      // Add category if new
      if (featureForm.category && !categories.features.includes(featureForm.category)) {
        setCategories({
          ...categories,
          features: [...categories.features, featureForm.category]
        });
      }

      setEditingFeature(null);
      setFeatureForm({ id: '', name: '', category: '', description: '' });
      
      // Mark as having unsaved changes and trigger auto-save
      setHasUnsavedChanges(true);
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleAutoSave();
      }, 1000);
      
      showNotification('Feature updated successfully');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFeature = async (featureId) => {
    const feature = features.find(f => f.id === featureId);
    const featureName = feature ? feature.name : 'this feature';
    
    if (window.confirm(`Are you sure you want to delete "${featureName}"? This will remove all service mappings for this feature and cannot be undone.`)) {
      setIsLoading(true);
      try {
        setFeatures(features.filter(f => f.id !== featureId));
        
        // Remove feature from mappings
        const updatedFeatureServices = { ...featureServices };
        delete updatedFeatureServices[featureId];
        setFeatureServices(updatedFeatureServices);
        
        // Mark as having unsaved changes and trigger auto-save
        setHasUnsavedChanges(true);
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
        autoSaveTimeoutRef.current = setTimeout(() => {
          handleAutoSave();
        }, 1000);
        
        showNotification(`Feature "${featureName}" deleted successfully`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Feature-Service mapping operations
  const handleFeatureServiceToggle = (featureId, serviceId) => {
    const currentValue = featureServices[featureId]?.[serviceId];
    const newValue = currentValue === 'Yes' ? undefined : 'Yes';
    
    setFeatureServices({
      ...featureServices,
      [featureId]: {
        ...featureServices[featureId],
        [serviceId]: newValue
      }
    });

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    // Clear existing auto-save timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new auto-save timeout (debounced)
    autoSaveTimeoutRef.current = setTimeout(() => {
      handleAutoSave();
    }, 1000); // Auto-save after 1 second of inactivity
  };

  // Auto-save function
  const handleAutoSave = async () => {
    setIsAutoSaving(true);
    
    const updatedData = {
      services,
      features,
      featureServices,
      categories
    };
    
    if (saveSecurityServicesData(updatedData)) {
      setHasUnsavedChanges(false);
      setIsAutoSaving(false);
      // Don't show notification for auto-save to avoid spam
    } else {
      setIsAutoSaving(false);
      showNotification('Auto-save failed', 'error');
    }
  };

  // Clean up auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Warn about unsaved changes when leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Save all changes
  const handleSaveChanges = () => {
    // Validate data before saving
    const validationErrors = validateData();
    if (validationErrors.length > 0) {
      showNotification(`Validation failed: ${validationErrors[0]}`, 'error');
      return;
    }

    const updatedData = {
      services,
      features,
      featureServices,
      categories
    };
    
    // Save using data manager
    if (saveSecurityServicesData(updatedData)) {
      setHasUnsavedChanges(false);
      showNotification('All changes saved successfully');
    } else {
      showNotification('Error saving changes', 'error');
    }
    
    if (onDataUpdate) {
      onDataUpdate(updatedData);
    }
  };

  // Reset to original data
  const handleResetChanges = () => {
    if (window.confirm('Are you sure you want to reset all changes? This will restore the original data and cannot be undone.')) {
      if (resetSecurityServicesData()) {
        // Reset to original data
        setServices(securityServicesData.services);
        setFeatures(securityServicesData.features);
        setFeatureServices(securityServicesData.featureServices);
        setCategories(securityServicesData.categories);
        setHasUnsavedChanges(false);
        showNotification('Changes reset successfully');
      } else {
        showNotification('Error resetting changes', 'error');
      }
    }
  };

  // Export data
  const handleExportData = () => {
    exportSecurityServicesData();
    showNotification('Data exported successfully');
  };

  // Import data
  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (file) {
      importSecurityServicesData(file)
        .then((data) => {
          setServices(data.services);
          setFeatures(data.features);
          setFeatureServices(data.featureServices);
          setCategories(data.categories);
          showNotification('Data imported successfully');
        })
        .catch((error) => {
          showNotification(`Import failed: ${error.message}`, 'error');
        });
    }
    // Reset file input
    event.target.value = '';
  };

  // Data validation function
  const validateData = () => {
    const errors = [];
    
    // Validate services
    if (services.length === 0) {
      errors.push('At least one service is required');
    }
    
    // Check for duplicate service IDs
    const serviceIds = services.map(s => s.id);
    const duplicateServiceIds = serviceIds.filter((id, index) => serviceIds.indexOf(id) !== index);
    if (duplicateServiceIds.length > 0) {
      errors.push(`Duplicate service IDs found: ${duplicateServiceIds.join(', ')}`);
    }
    
    // Validate features
    if (features.length === 0) {
      errors.push('At least one feature is required');
    }
    
    // Check for duplicate feature IDs
    const featureIds = features.map(f => f.id);
    const duplicateFeatureIds = featureIds.filter((id, index) => featureIds.indexOf(id) !== index);
    if (duplicateFeatureIds.length > 0) {
      errors.push(`Duplicate feature IDs found: ${duplicateFeatureIds.join(', ')}`);
    }
    
    // Validate feature-service mappings
    Object.keys(featureServices).forEach(featureId => {
      if (!features.find(f => f.id === featureId)) {
        errors.push(`Feature mapping exists for non-existent feature: ${featureId}`);
      }
      
      Object.keys(featureServices[featureId]).forEach(serviceId => {
        if (!services.find(s => s.id === serviceId)) {
          errors.push(`Service mapping exists for non-existent service: ${serviceId}`);
        }
      });
    });
    
    return errors;
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Services Matrix Admin</h1>
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-500">Manage services, features, and their relationships</p>
                {hasUnsavedChanges && (
                  <span className="px-2 py-1 text-xs bg-amber-100 text-amber-800 rounded-full font-medium">
                    Unsaved Changes
                  </span>
                )}
                {isAutoSaving && (
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full font-medium">
                    Auto-saving...
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportData}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Download className="w-4 h-4 mr-2 inline" />
              Export
            </button>
            <label className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer">
              <Upload className="w-4 h-4 mr-2 inline" />
              Import
              <input
                type="file"
                accept=".json"
                onChange={handleImportData}
                className="hidden"
              />
            </label>
            <button
              onClick={handleResetChanges}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Reset Changes
            </button>
            <button
              onClick={handleSaveChanges}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Save className="w-4 h-4 mr-2 inline" />
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mx-6 mt-4 p-4 rounded-md ${
          notification.type === 'error' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
        }`}>
          <div className="flex items-center">
            {notification.type === 'error' ? (
              <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            ) : (
              <Check className="w-5 h-5 text-green-400 mr-2" />
            )}
            <p className={`text-sm ${notification.type === 'error' ? 'text-red-800' : 'text-green-800'}`}>
              {notification.message}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white px-6">
        <div className="flex space-x-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('services')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'services'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex flex-col items-center">
              <span>Services ({services.length})</span>
              <span className="text-xs text-gray-400 mt-1">Manage service definitions</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('features')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'features'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex flex-col items-center">
              <span>Features ({features.length})</span>
              <span className="text-xs text-gray-400 mt-1">Manage feature catalog</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('matrix')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'matrix'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex flex-col items-center">
              <span>Service Mapping</span>
              <span className="text-xs text-gray-400 mt-1">Link features to services</span>
            </div>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'services' && (
          <ServicesTab
            services={services}
            serviceForm={serviceForm}
            setServiceForm={setServiceForm}
            editingService={editingService}
            showAddService={showAddService}
            setShowAddService={setShowAddService}
            onAdd={handleAddService}
            onEdit={handleEditService}
            onUpdate={handleUpdateService}
            onDelete={handleDeleteService}
            onCancel={() => {
              setEditingService(null);
              setShowAddService(false);
              setServiceForm({ id: '', name: '', category: '', description: '' });
            }}
          />
        )}

        {activeTab === 'features' && (
          <FeaturesTab
            features={features}
            categories={categories.features}
            featureForm={featureForm}
            setFeatureForm={setFeatureForm}
            editingFeature={editingFeature}
            showAddFeature={showAddFeature}
            setShowAddFeature={setShowAddFeature}
            isLoading={isLoading}
            onAdd={handleAddFeature}
            onEdit={handleEditFeature}
            onUpdate={handleUpdateFeature}
            onDelete={handleDeleteFeature}
            onCancel={() => {
              setEditingFeature(null);
              setShowAddFeature(false);
              setFeatureForm({ id: '', name: '', category: '', description: '' });
            }}
          />
        )}

        {activeTab === 'matrix' && (
          <MatrixTab
            services={services}
            features={features}
            featureServices={featureServices}
            onToggle={handleFeatureServiceToggle}
          />
        )}
      </div>
    </div>
  );
};

// Services Tab Component
const ServicesTab = ({ 
  services, 
  serviceForm, 
  setServiceForm, 
  editingService, 
  showAddService, 
  setShowAddService,
  onAdd, 
  onEdit, 
  onUpdate, 
  onDelete, 
  onCancel 
}) => (
  <div className="p-6">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-lg font-medium text-gray-900">Manage Services</h2>
      <button
        onClick={() => setShowAddService(true)}
        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <Plus className="w-4 h-4 mr-2 inline" />
        Add Service
      </button>
    </div>

    {/* Add Service Form */}
    {showAddService && (
      <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Service</h3>
        <ServiceForm
          form={serviceForm}
          setForm={setServiceForm}
          onSubmit={onAdd}
          onCancel={onCancel}
          submitLabel="Add Service"
        />
      </div>
    )}

    {/* Services List */}
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {services.map((service) => (
              <tr key={service.id} className="hover:bg-gray-50">
                {editingService === service.id ? (
                  <td colSpan={4} className="px-6 py-4">
                    <ServiceForm
                      form={serviceForm}
                      setForm={setServiceForm}
                      onSubmit={onUpdate}
                      onCancel={onCancel}
                      submitLabel="Update Service"
                    />
                  </td>
                ) : (
                  <>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{service.name}</div>
                        <div className="text-sm text-gray-500">ID: {service.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {service.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{service.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => onEdit(service)}
                        className="text-blue-600 hover:text-blue-900 mr-3 p-1 rounded hover:bg-blue-50"
                        title="Edit service"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(service.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                        title="Delete service"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Features Tab Component
const FeaturesTab = ({ 
  features, 
  categories,
  featureForm, 
  setFeatureForm, 
  editingFeature, 
  showAddFeature, 
  setShowAddFeature,
  isLoading,
  onAdd, 
  onEdit, 
  onUpdate, 
  onDelete, 
  onCancel 
}) => {
  // Debug logging
  console.log('FeaturesTab render:', { 
    featuresCount: features.length, 
    categoriesCount: categories.length,
    editingFeature,
    showAddFeature,
    isLoading
  });
  
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-gray-900">Manage Features</h2>
        <button
          onClick={() => setShowAddFeature(true)}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2 inline" />
          Add Feature
        </button>
      </div>

      {/* Add Feature Form */}
      {showAddFeature && (
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Feature</h3>
          <FeatureForm
            form={featureForm}
            setForm={setFeatureForm}
            categories={categories}
            isLoading={isLoading}
            onSubmit={onAdd}
            onCancel={onCancel}
            submitLabel="Add Feature"
          />
        </div>
      )}

      {/* Features List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feature</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {features.map((feature) => (
                <tr key={feature.id} className="hover:bg-gray-50">
                  {editingFeature === feature.id ? (
                    <td colSpan={4} className="px-6 py-4">
                      <FeatureForm
                        form={featureForm}
                        setForm={setFeatureForm}
                        categories={categories}
                        isLoading={isLoading}
                        onSubmit={onUpdate}
                        onCancel={onCancel}
                        submitLabel="Update Feature"
                      />
                    </td>
                  ) : (
                    <>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{feature.name}</div>
                          <div className="text-sm text-gray-500">ID: {feature.id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                          {feature.category || 'Uncategorized'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{feature.description || 'No description'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => onEdit(feature)}
                          disabled={isLoading}
                          className="text-blue-600 hover:text-blue-900 mr-3 p-1 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Edit feature"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onDelete(feature.id)}
                          disabled={isLoading}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete feature"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Matrix Tab Component
const MatrixTab = ({ services, features, featureServices, onToggle }) => (
  <div className="p-6">
    <div className="mb-6">
      <h2 className="text-lg font-medium text-gray-900">Service Mapping</h2>
      <p className="text-sm text-gray-500 mt-1">Click cells to assign features to services. Changes are auto-saved.</p>
    </div>

    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-auto max-h-96">
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[300px]">
                Feature
              </th>
              {services.map((service) => (
                <th key={service.id} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                  <div className="text-xs leading-tight">{service.name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {features.map((feature, index) => (
              <tr key={feature.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}>
                <td className="px-4 py-3 sticky left-0 bg-white z-10 border-r border-gray-200 min-w-[300px]">
                  <div className="text-sm font-medium text-gray-900">{feature.name}</div>
                  <div className="text-xs text-gray-500">{feature.category}</div>
                </td>
                {services.map((service) => {
                  const isActive = featureServices[feature.id]?.[service.id] === 'Yes';
                  return (
                    <td key={`${feature.id}-${service.id}`} className="px-3 py-3 text-center">
                      <button
                        onClick={() => onToggle(feature.id, service.id)}
                        className={`w-8 h-8 rounded-full border-2 transition-colors ${
                          isActive
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'bg-white border-gray-300 text-gray-400 hover:border-gray-400'
                        }`}
                        title={`${feature.name} - ${service.name}: ${isActive ? 'Available' : 'Not Available'}`}
                      >
                        {isActive && <Check className="w-4 h-4 mx-auto" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

// Service Form Component
const ServiceForm = ({ form, setForm, onSubmit, onCancel, submitLabel }) => (
  <div className="grid grid-cols-1 gap-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter service name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
        <input
          type="text"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          placeholder="ASI Services"
        />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
      <textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        rows={2}
        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter service description"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">ID (auto-generated if empty)</label>
      <input
        type="text"
        value={form.id}
        onChange={(e) => setForm({ ...form, id: e.target.value })}
        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        placeholder="service_id (optional)"
      />
    </div>
    <div className="flex justify-end space-x-3">
      <button
        onClick={onCancel}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Cancel
      </button>
      <button
        onClick={onSubmit}
        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        {submitLabel}
      </button>
    </div>
  </div>
);

// Feature Form Component
const FeatureForm = ({ form, setForm, categories, isLoading, onSubmit, onCancel, submitLabel }) => (
  <div className="grid grid-cols-1 gap-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Feature Name *</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter feature name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
        <select
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select category</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <input
          type="text"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 mt-2"
          placeholder="Or enter new category"
        />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
      <textarea
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        rows={2}
        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        placeholder="Enter feature description (optional)"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">ID (auto-generated if empty)</label>
      <input
        type="text"
        value={form.id}
        onChange={(e) => setForm({ ...form, id: e.target.value })}
        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        placeholder="feature_id (optional)"
      />
    </div>
    <div className="flex justify-end space-x-3">
      <button
        onClick={onCancel}
        disabled={isLoading}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Cancel
      </button>
      <button
        onClick={onSubmit}
        disabled={isLoading}
        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
      >
        {isLoading && (
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {isLoading ? 'Processing...' : submitLabel}
      </button>
    </div>
  </div>
);

export default SecurityServicesAdmin; 